import { API_BASE } from '@/lib/api';

declare global {
  interface Navigator {
    setAppBadge?: (count?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
    standalone?: boolean;
  }
  interface Window {
    PushManager?: unknown;
  }
}

const SW_PATH = '/sw.js';
const SW_SCOPE = '/';

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let listenersAttached = false;

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** True when an existing subscription was created under the server's current
 * VAPID public key (its applicationServerKey). A mismatch means the push service
 * will reject every message for it (FCM 403 SENDER_ID_MISMATCH), so it must be
 * re-created with the current key. */
function subscriptionUsesCurrentKey(sub: PushSubscription | null, expectedKey: Uint8Array): boolean {
  try {
    const existing = (sub?.options?.applicationServerKey ?? null) as ArrayBuffer | null;
    if (!existing) return false;
    const bytes = new Uint8Array(existing);
    if (bytes.length !== expectedKey.length) return false;
    for (let i = 0; i < bytes.length; i += 1) {
      if (bytes[i] !== expectedKey[i]) return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** True only in a context that can actually do web push. */
export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && typeof (Notification as unknown) !== 'undefined';
}

// The tenant session is carried BOTH in sessionStorage (cleared when the tab or
// the installed PWA is closed) and in an httpOnly `session_token` cookie (which
// the Worker prefers and which survives reopen). Because the cookie is httpOnly
// we cannot read it from JS, so we also track an explicit authenticated flag set
// by the app whenever it validates a session. The push endpoints are
// authenticated and work over the cookie, so "authenticated" here must be true
// for an installed PWA reopened via its persistent cookie - not only when a
// sessionStorage token happens to survive.
let sessionAuthenticated = false;

/** Set/clear the app-level authenticated flag (called by the auth bootstrap). */
export function setSessionAuthenticated(value: boolean): void {
  sessionAuthenticated = value;
}

/** True when a tenant session is present (token in storage OR cookie-based app session). */
export function isAuthenticated(): boolean {
  return typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('access_token')
    || sessionAuthenticated;
}

/**
 * Register the service worker exactly once. Idempotent and de-duplicated across
 * renders/tabs; a single worker is shared per origin.
 */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return Promise.resolve(null);
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(SW_PATH, { scope: SW_SCOPE })
      .catch((err) => {
        console.error('Service worker registration failed:', err);
        return null;
      });
  }
  return registrationPromise;
}

/** Push the authoritative badge count to the service worker so the badge stays
 * correct while the app is backgrounded or closed. Non-blocking; the worker
 * falls back to its own payload/received handling if unavailable. */
function postBadgeToSw(count: number): void {
  try {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
    navigator.serviceWorker.controller.postMessage({ type: 'set-badge', count });
  } catch (err) {
    void err;
  }
}

function postMessageToSw(message: Record<string, unknown>): void {
  try {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
    navigator.serviceWorker.controller.postMessage(message);
  } catch (err) {
    void err;
  }
}

/**
 * Ensure the current user has a valid push subscription registered with the
 * backend. Handles three cases without ever requesting notification permission
 * automatically:
 *
 * 1. permission !== "granted" → do nothing
 * 2. permission === "granted" + no subscription → create one with the current
 *    VAPID key and save it to the backend
 * 3. permission === "granted" + existing subscription → compare its
 *    applicationServerKey against the server's current VAPID public key; if
 *    different, unsubscribe and re-create; always upsert to the backend to
 *    ensure DB consistency (handles rows deactivated by FCM 403)
 *
 * Race safety:
 * - inFlightSubscriptionRepair (Promise | null) prevents duplicate simultaneous
 *   repairs. It is set non-null BEFORE the async work starts and cleared in the
 *   finally block.
 * - subscriptionRepairSucceeded is set to true ONLY after the entire async
 *   operation (verify/repair/save) completes successfully.
 * - On transient failure: inFlightSubscriptionRepair clears, and
 *   subscriptionRepairSucceeded stays false → next refreshBadge() call retries.
 * - On success: subscriptionRepairSucceeded stays true → subsequent calls
 *   skip the check entirely.
 */
let inFlightSubscriptionRepair: Promise<boolean> | null = null;
let subscriptionRepairSucceeded = false;

async function saveSubscriptionToServer(sub: PushSubscription): Promise<boolean> {
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    console.warn('[PushDebug] Subscription incomplete — cannot save');
    return false;
  }
  const subRes = await pushFetch('/notifications/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      userAgent: navigator.userAgent,
    }),
  });
  if (subRes.ok) {
    console.log('[PushDebug] Subscription saved to server');
    return true;
  }
  console.warn('[PushDebug] Subscription save failed:', subRes.status);
  return false;
}

async function ensureValidSubscription(): Promise<boolean> {
  if (subscriptionRepairSucceeded) return true;
  if (inFlightSubscriptionRepair) return inFlightSubscriptionRepair;

  const promise = (async (): Promise<boolean> => {
    try {
      const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
      if (permission !== 'granted') {
        console.log('[PushDebug] permission !== "granted" — skipping subscription repair');
        return false;
      }

      const pub = await getVapidPublicKey();
      if (pub.configured === false) {
        console.log('[PushDebug] VAPID not configured — skipping subscription repair');
        return false;
      }
      const expectedKeyBytes = urlBase64ToUint8Array(pub.key);

      const pm = await getPushManager();
      if (!pm) {
        console.log('[PushDebug] PushManager unavailable — skipping subscription repair');
        return false;
      }

      const sub = await pm.getSubscription();

      // Case: permission granted + no subscription → create one
      if (!sub) {
        console.log('[PushDebug] No push subscription found. Creating fresh subscription...');
        const newSub = await pm.subscribe({
          userVisibleOnly: true,
          applicationServerKey: expectedKeyBytes,
        });
        const saved = await saveSubscriptionToServer(newSub);
        if (saved) subscriptionRepairSucceeded = true;
        return saved;
      }

      // Case: permission granted + existing subscription → check key
      const isCurrent = subscriptionUsesCurrentKey(sub, expectedKeyBytes);
      console.log('[PushDebug] subscription key matches current VAPID key =', isCurrent);

      if (!isCurrent) {
        console.warn('[PushDebug] Stale subscription detected. Re-subscribing with current VAPID key...');
        await sub.unsubscribe().catch(() => {});
        const freshSub = await pm.subscribe({
          userVisibleOnly: true,
          applicationServerKey: expectedKeyBytes,
        });
        const saved = await saveSubscriptionToServer(freshSub);
        if (saved) subscriptionRepairSucceeded = true;
        return saved;
      }

      // Subscription exists and key matches — still ensure the backend has it
      // (handles the case where the DB row was deactivated by a FCM 403 but
      // the browser's subscription object survived)
      const saved = await saveSubscriptionToServer(sub);
      if (saved) subscriptionRepairSucceeded = true;
      return saved;
    } catch (err) {
      console.warn('[PushDebug] Subscription repair failed (will retry on next load):', err);
      return false;
    } finally {
      inFlightSubscriptionRepair = null;
    }
  })();

  inFlightSubscriptionRepair = promise;
  return promise;
}

/** Tag format must match the Worker's push tag (`entityType:entityId`). */
function shadeNotificationTag(entityType: string, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/**
 * Dismiss the native shade notification for a specific record after it was
 * marked read. On Android/ColorOS the launcher badge is derived from active,
 * undismissed shade notifications, so dismissing here keeps the launcher count
 * in step with the real unread count (read-one must lower the badge, not just
 * the in-app counter). No-op when no matching notification is active.
 */
export function dismissShadeNotification(entityType: string, entityId: string): void {
  if (!entityType || !entityId) return;
  postMessageToSw({ type: 'notification-read', tag: shadeNotificationTag(entityType, entityId) });
}

/** Dismiss all active shade notifications after "mark all read" succeeds. */
export function dismissAllShadeNotifications(): void {
  postMessageToSw({ type: 'notifications-read-all' });
}

interface SetBadgeCountResponse {
  success?: boolean;
  data?: { count?: number };
}

// Auto-sync the authenticated flag at boot from the cookie-based session so push
// works for an installed PWA that was reopened (sessionStorage cleared). The
// shared AuthContext also calls setSessionAuthenticated(true) when a session is
// validated.

/** Fetch the current user's unread count and update the app icon badge. Pass an
 * already-known count to skip the fetch and set the badge directly (used by the
 * notification bell, which already has the number). */
export async function refreshBadge(knownCount?: number): Promise<void> {
  try {
    if (!isAuthenticated()) {
      clearBadge();
      return;
    }
    if (!subscriptionRepairSucceeded && !inFlightSubscriptionRepair) {
      void ensureValidSubscription();
    }
    if (knownCount !== undefined) {
      setBadge(knownCount);
      return;
    }
    const res = await pushFetch('/notifications/unread-count');
    if (!res.ok) {
      // 401/403 here just means no push session - a logged-out or cookie-less
      // context. Not fatal; leave the badge state unchanged.
      if (res.status === 401 || res.status === 403) sessionAuthenticated = false;
      return;
    }
    const count = Number(res.body?.data?.count ?? 0);
    setBadge(count);
  } catch (err) {
    // Not fatal - ignore badge refresh errors.
    void err;
  }
}

/**
 * Diagnostic snapshot of the Badging API environment for this device/PWA.
 * Logged ONCE (never repeatedly and never shown in UI) so real-device issues can
 * be pinpointed to the browser, installed PWA, OS, or app code. Expected
 * platform limits (e.g. no Badging API on iOS) are reported as facts, not errors.
 */
let badgeEnvReported = false;

function logBadgeDiagnostics(unreadCount: number, context: string): void {
  if (badgeEnvReported) return;
  badgeEnvReported = true;
  try {
    const setAppBadgeSupported = 'setAppBadge' in navigator;
    const clearAppBadgeSupported = 'clearAppBadge' in navigator;
    const sw = navigator.serviceWorker;
    const standalone = !!(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const diag = {
      context,
      notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A',
      setAppBadgeSupported,
      clearAppBadgeSupported,
      badgeStrategy: isAndroid ? 'notification-shade (launcher derives badge)' : (isIOS && standalone ? 'navigator.setAppBadge' : 'navigator.setAppBadge (if supported)'),
      serviceWorkerSupported: 'serviceWorker' in navigator,
      serviceWorkerRegistered: !!(sw && typeof sw.controller === 'object' && sw.controller !== null),
      unreadCount,
      standalone,
      displayMode: standalone ? 'standalone' : 'browser-tab',
      platform: isAndroid ? 'android' : (isIOS ? 'ios' : 'other'),
    };
    console.log('[PushDebug] badge diagnostic:', diag);
  } catch (err) {
    console.warn('[Departmental Labour App] badge diagnostic unavailable:', err);
  }
}

/** Invoke the Badging API directly on `navigator` to preserve the correct
 * receiver context. Extracting the method into a variable and calling it
 * separately causes "Illegal invocation" because the native method requires
 * `this` to be the Navigator object. */
function setAppBadge(count: number): Promise<void> | void {
  const nav = navigator as unknown as { setAppBadge?: (count: number) => Promise<void> };
  if (typeof nav.setAppBadge === 'function') {
    return nav.setAppBadge(count);
  }
}

function clearAppBadge(): Promise<void> | void {
  const nav = navigator as unknown as { clearAppBadge?: () => Promise<void> };
  if (typeof nav.clearAppBadge === 'function') {
    return nav.clearAppBadge();
  }
}

let badgeUnavailableReported = false;

function setBadge(count: number): void {
  const total = Math.max(0, Math.floor(count || 0));
  logBadgeDiagnostics(total, 'setBadge');
  postBadgeToSw(total);
  const hasSet = 'setAppBadge' in navigator;
  const hasClear = 'clearAppBadge' in navigator;
  try {
    if (total > 0) {
      if (hasSet) {
        const p = setAppBadge(total);
        if (p && typeof p.then === 'function') {
          p.then(
            () => console.log('[PushDebug] setAppBadge ok:', total),
            (err) => console.warn('[PushDebug] setAppBadge error (expected on Android):', err)
          );
        }
      } else if (!badgeUnavailableReported) {
        badgeUnavailableReported = true;
        console.warn('[PushDebug] setAppBadge not available - badge comes from notifications in the shade.');
      }
    } else if (hasClear) {
      const p = clearAppBadge();
      if (p && typeof p.then === 'function') {
        p.then(
          () => console.log('[PushDebug] clearAppBadge ok'),
          (err) => console.warn('[PushDebug] clearAppBadge error:', err)
        );
      }
    }
  } catch (err) {
    // Badge not supported in this context; ignore.
    void err;
  }
}

export function clearBadge(): void {
  if (!('clearAppBadge' in navigator)) return;
  try {
    const p = clearAppBadge();
    if (p && typeof p.then === 'function') {
      p.then(
        () => console.log('[PushDebug] clearAppBadge ok'),
        (err) => console.warn('[PushDebug] clearAppBadge error:', err)
      );
    }
  } catch (err) {
    void err;
  }
}

/**
 * Authenticated fetch for push endpoints that MUST NOT use the shared api client:
 * the shared client auto-redirects to the login page on a 401, but a 401 here
 * simply means "not signed in for push" and must be reported as such (STEP 11).
 * Sends the cookie (which carries the tenant session even for an installed PWA
 * reopened after sessionStorage was cleared) and any surviving bearer token.
 */
type PushHttpResult =
  | { ok: true; body: any }
  | { ok: false; status: number; body?: any };

async function pushFetch(urlPath: string, options: RequestInit = {}): Promise<PushHttpResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  const token =
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('access_token') : null) || null;
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const res = await fetch(`${API_BASE}${urlPath}`, {
      ...options,
      headers,
      credentials: 'include',
    });
    const body = await res.json().catch(() => ({}));
    return { ok: res.ok, status: res.status, body };
  } catch (err) {
    // Network failure (offline / DNS / proxy). Not an auth or config problem.
    return { ok: false, status: 0, body: err };
  }
}

async function getVapidPublicKey(): Promise<
  { configured: true; key: string }
  | { configured: false; reason: 'not-configured' | 'auth' | 'network' }
> {
  if (!isAuthenticated()) {
    return { configured: false, reason: 'auth' };
  }
  const res = await pushFetch('/notifications/push/public-key');
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      console.warn('Push VAPID public key request was not authorized:', res.status);
      return { configured: false, reason: 'auth' };
    }
    if (res.status === 0) {
      console.warn('Push VAPID public key request failed (network):', res.body);
      return { configured: false, reason: 'network' };
    }
    console.warn('Push VAPID public key request failed:', res.status, res.body);
    return { configured: false, reason: 'network' };
  }
  const key = res.body?.data?.publicKey;
  if (typeof key === 'string' && key.length > 0) return { configured: true, key };
  // The endpoint answered successfully but returned no key: VAPID is genuinely
  // not configured on the server (STEP 14).
  console.warn('Push VAPID public key is empty; Web Push not configured server-side.');
  return { configured: false, reason: 'not-configured' };
}

async function getPushManager(): Promise<PushManager | null> {
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager;
}

/** Current active push subscription for this origin/device, if any. */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  const pm = await getPushManager();
  if (!pm) return null;
  return pm.getSubscription();
}

export type PushStatus = {
  supported: boolean;
  permission: NotificationPermission;
  /** True when the browser permission is granted AND an active server subscription exists. */
  enabled: boolean;
  /** True when the served app is running in installed/standalone mode. */
  standalone: boolean;
};

export async function getPushStatus(): Promise<PushStatus> {
  const supported = pushSupported();
  if (!supported) return { supported: false, permission: 'denied', enabled: false, standalone: false };

  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';

  if (!isAuthenticated()) {
    // No tenant session (cookie or token). Report the browser permission state
    // only; the device is still capable, so keep `supported` true.
    return { supported, permission, enabled: false, standalone: !!standaloneMode() };
  }

  let enabled = false;
  if (permission === 'granted') {
    try {
      const sub = await getPushSubscription();
      enabled = !!sub;
    } catch (err) {
      enabled = false;
      void err;
    }
  }

  return { supported, permission, enabled, standalone: !!standaloneMode() };
}

function standaloneMode(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as { standalone?: boolean }).standalone === true;
}

/**
 * Development diagnostics for the device push chain (STEP 2/3/4). Logs only safe
 * metadata - never endpoints/keys/recipient identity. Helps pinpoint whether a
 * failure is registration, permission, subscription, or a later layer.
 */
function logSwRegistrationDiagnostics(reg: ServiceWorkerRegistration | null): void {
  try {
    const controller = navigator.serviceWorker?.controller;
    const state = reg?.active ? 'active' : reg?.waiting ? 'waiting' : reg?.installing ? 'installing' : 'unknown';
    console.log('[PushDebug] SW registration:', {
      registered: reg != null,
      scope: reg?.scope ?? null,
      state,
      active: !!reg?.active,
      waiting: !!reg?.waiting,
      installing: !!reg?.installing,
      controlled: !!controller,
    });
  } catch (err) {
    console.warn('[PushDebug] SW registration diagnostic unavailable:', err);
  }
}

function logNotificationPermissionDiagnostics(): void {
  try {
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'N/A';
    console.log('[PushDebug] notification permission =', permission);
  } catch (err) {
    console.warn('[PushDebug] notification permission diagnostic unavailable:', err);
  }
}

async function logSubscriptionDiagnostics(): Promise<void> {
  try {
    const reg = await registerServiceWorker();
    if (!reg || !reg.pushManager) {
      console.log('[PushDebug] service worker ready =', false);
      console.log('[PushDebug] subscription exists =', false, '(service-worker-unavailable)');
      return;
    }
    console.log('[PushDebug] service worker ready =', !!reg.active);
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      console.log('[PushDebug] subscription exists =', false);
      return;
    }
    let endpointHost = '';
    try {
      endpointHost = new URL(sub.endpoint).host;
    } catch {
      // keep host empty; not actionable
    }
    console.log('[PushDebug] subscription exists =', true);
    console.log('[PushDebug] subscription endpoint host =', endpointHost);
    console.log('[PushDebug] subscription has keys =', !!(sub.getKey && sub.getKey('p256dh') && sub.getKey('auth')));
  } catch (err) {
    console.log('[PushDebug] subscription diagnostics unavailable:', err);
  }
}

/** Stable machine-readable error codes so the UI can show honest, specific copy (STEP 11). */
export type PushEnableErrorCode =
  | 'unsupported'
  | 'not-authorized'
  | 'permission-denied'
  | 'not-configured'
  | 'auth'
  | 'network'
  | 'push-unavailable'
  | 'incomplete'
  | 'unknown';

export type PushEnableResult =
  | { ok: true }
  | { ok: false; code: PushEnableErrorCode; error?: string };

/**
 * Enable push notifications for the currently authenticated user on this device:
 * request permission, create the push subscription, and register it server-side
 * (associated with the authenticated user's account).
 */
export async function enableNotifications(): Promise<PushEnableResult> {
  if (!pushSupported()) return { ok: false, code: 'unsupported', error: 'Web push is not supported in this browser.' };
  if (!isAuthenticated()) {
    return { ok: false, code: 'not-authorized', error: 'You must be signed in to enable notifications.' };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, code: 'permission-denied', error: 'Notification permission was not granted.' };
    }

    const pub = await getVapidPublicKey();
    if (pub.configured === false) {
      if (pub.reason === 'auth') {
        sessionAuthenticated = false;
        return { ok: false, code: 'auth', error: 'Your session could not be verified. Please sign in again and try again.' };
      }
      if (pub.reason === 'not-configured') {
        return { ok: false, code: 'not-configured', error: 'Push is not configured on the server yet.' };
      }
      return { ok: false, code: 'network', error: 'Unable to reach the server. Please check your connection and try again.' };
    }
    const publicKey = pub.key;
    const expectedKeyBytes = urlBase64ToUint8Array(publicKey);

    const pm = await getPushManager();
    if (!pm) return { ok: false, code: 'push-unavailable', error: 'Push is not available in this browser.' };

    let sub = await pm.getSubscription();
    if (sub && !subscriptionUsesCurrentKey(sub, expectedKeyBytes)) {
      // The existing subscription was registered under a different (previous or
      // missing) applicationServerKey, so the push service will reject sends for
      // it forever (FCM 403). Re-create it with the current VAPID key.
      console.warn('[PushDebug] Existing push subscription uses a stale applicationServerKey; re-creating it.');
      await sub.unsubscribe().catch(() => {});
      sub = null;
    }
    if (!sub) {
      sub = await pm.subscribe({
        userVisibleOnly: true,
        applicationServerKey: expectedKeyBytes,
      });
    }

    const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    const endpoint = json.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!endpoint || !p256dh || !auth) return { ok: false, code: 'incomplete', error: 'The push subscription is incomplete.' };

    const subRes = await pushFetch('/notifications/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint, p256dh, auth, userAgent: navigator.userAgent }),
    });
    if (!subRes.ok) {
      console.error('Push subscribe failed:', subRes.status, subRes.body);
      if (subRes.status === 401 || subRes.status === 403) {
        sessionAuthenticated = false;
        return { ok: false, code: 'auth', error: 'Your session could not be verified. Please sign in again and try again.' };
      }
      return { ok: false, code: 'network', error: 'Unable to save this device. Please try again.' };
    }

    await refreshBadge();
    return { ok: true };
  } catch (err: any) {
    console.error('Enable notifications failed:', err);
    return { ok: false, code: 'unknown', error: err?.message || 'Failed to enable notifications.' };
  }
}

/**
 * Disable push notifications for the authenticated user on this device. Only the
 * current device's subscription is deactivated; other devices of the same user
 * are unaffected, and the user's account is untouched.
 */
export async function disableNotifications(): Promise<{ ok: boolean; error?: string }> {
  if (!isAuthenticated()) {
    clearBadge();
    return { ok: true };
  }
  try {
    const sub = await getPushSubscription();
    if (sub) {
      const json = sub.toJSON() as { endpoint?: string };
      if (json.endpoint) {
        await pushFetch('/notifications/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: json.endpoint }),
        }).catch(() => {});
      }
      await sub.unsubscribe().catch(() => {});
    }
    clearBadge();
    return { ok: true };
  } catch (err: any) {
    console.error('Disable notifications failed:', err);
    return { ok: false, error: err?.message || 'Failed to disable notifications.' };
  }
}

function handleServiceWorkerMessage(event: MessageEvent): void {
  const data = event.data || {};
  if (data.type === 'push-received') {
    void refreshBadge();
  } else if (data.type === 'navigate' && typeof data.url === 'string') {
    window.location.assign(data.url);
  }
}

/**
 * Attach global listeners and register the service worker once. Safe to call
 * multiple times; listeners and registration are de-duplicated. Keeps the
 * badge in sync across the app lifecycle (load, focus, login, reads, pushes).
 */
export function initPushClient(): void {
  logNotificationPermissionDiagnostics();
  registerServiceWorker().then((reg) => {
    logSwRegistrationDiagnostics(reg);
    if (reg && reg.active && navigator.serviceWorker?.controller) {
      // push any stored count the SW might have once connected
      void refreshBadge();
    }
  });
  void logSubscriptionDiagnostics();

  if ('serviceWorker' in navigator && !listenersAttached) {
    listenersAttached = true;
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    // Update the badge whenever the tab gains focus or becomes visible.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshBadge();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', () => void refreshBadge());
  }
}
